import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import {
  subscribeMeetingIntelEvents,
  type MeetingIntelEvent,
} from "../meetingIntelligenceRealtime";
import type {
  MeetingMappingUnmarkPayload,
  ReadAiMeeting,
} from "../meetingIntelligence.types";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import {
  MEETING_INTEL_SERVER_CAP,
  rowsReturned,
  isAtCap,
  type LoadCap,
} from "shared/truncation";
import { swallowedFailure } from "../../devops/shared/swallowedFind";
import { isAdmin } from "auth/tier";
import { isSessionEnded } from "stores/sessionGuard";

/**
 * TWO DIFFERENT ANALYSES RUN OVER A MEETING, AND THIS PAGE MUST NOT CONFLATE
 * THEM.
 *
 *  1. **HQ's own analysis** — the seven fused `meeting_analysis` tasks
 *     (summary, topics, key questions, risks, decisions, follow-ups,
 *     happiness). None of them need a client, which is precisely why an
 *     internal meeting gets them (git show 23f8d85^:plan/2026-09-03_internal-meetings.md §2.2).
 *     This is what this page is ABOUT, so it is the page's primary state.
 *     It lives in the `meeting_analysis` table, read through the
 *     `meeting-analysis` service.
 *
 *  2. **Gantry's deep analysis** — the lease lifecycle in
 *     `read_ai_meetings.analysisState` (pending | claimed | analyzed | failed |
 *     skipped). It is a DIFFERENT job by a DIFFERENT producer and it never
 *     moves when HQ writes its `meeting_analysis` rows. Migration
 *     20260825000001 also backfilled the entire pre-2026-08-25 corpus to
 *     `'skipped'`, so on that column most history reads "skipped" regardless of
 *     what was actually analysed.
 *
 * The first version of this page rendered (2) under labels that promised (1) —
 * "Awaiting Analysis" on a meeting whose seven analysis rows were already
 * written, and a grey "skipped" chip on every meeting older than the backfill,
 * directly beneath an alert saying these meetings are analysed for their own
 * record. Everything below keeps the two named apart, and anything the page
 * cannot determine reads as `unknown` rather than as a verdict.
 */

/**
 * HQ's own `meeting_analysis` state for one meeting — the page's headline.
 *
 * FIVE states, not four, because a meeting's seven analyses do NOT succeed or
 * fail together (see {@link HQ_ANALYSIS_TASK_KEYS}):
 *
 *  - `analyzed` — every one of the seven landed with `status = 'ok'`.
 *  - `partial`  — some landed and some did not. Real, and previously invisible:
 *                 a task an admin routed to its own provider runs on its own
 *                 call, and each fused row is persisted best-effort, so one
 *                 provider outage takes some of the seven and not the rest.
 *  - `pending`  — no rows at all yet.
 *  - `failed`   — rows exist and NOT ONE is `ok`.
 *  - `unknown`  — the probe has not answered (or could not be read).
 */
export type HqAnalysisState =
  | "analyzed"
  | "partial"
  | "pending"
  | "failed"
  | "unknown";

/** Gantry's deep-analysis lease state, normalised for display. */
export type DeepAnalysisState = "analyzed" | "pending" | "failed" | "skipped";

/** The header's filter, over HQ's analysis (not the deep one). */
export type InternalAnalysisFilter = "all" | HqAnalysisState;

export type InternalMeetingSortKey =
  | "title"
  | "ownerName"
  | "startTime"
  | "hqAnalysis"
  | "deepAnalysis"
  | "actionItemCount"
  | "createdAt";

export type InternalMeetingSortDir = SortDir;

/**
 * The `meeting_analysis` task keys HQ writes for every meeting — the API's
 * `MEETING_ANALYSIS_TASKS` (read-ai/meeting-analysis.tasks.ts), in its order.
 *
 * THIS USED TO BE ONE KEY, AND THE REASON GIVEN FOR THAT WAS FALSE. The old
 * comment claimed `meeting.summary` was an anchor — "there is no path that
 * writes the other six without it". There is. The seven are normally sent as
 * ONE fused call, but a task an admin has routed in `llm_task_routes` is
 * excluded from the bundle and run on its OWN provider
 * (`meeting-analysis.fused.ts`, and the caller in client-intelligence), and
 * every resulting row is persisted best-effort. So the summary can be the one
 * task that failed while six landed — which the old probe rendered as "HQ
 * Analysis Failed" for a meeting that has six analyses — and the summary can
 * land while another task failed, which it rendered as a clean "analyzed".
 *
 * Probing all seven is what makes {@link HqAnalysisState} an honest answer.
 */
export const HQ_ANALYSIS_TASK_KEYS = [
  "meeting.summary",
  "meeting.happiness-score",
  "meeting.topics",
  "meeting.key-questions",
  "meeting.risks",
  "meeting.decisions",
  "meeting.follow-ups",
] as const;

/** Sort keys that read best newest-first on the first click. */
const DESC_FIRST: ReadonlySet<InternalMeetingSortKey> = new Set([
  "startTime",
  "createdAt",
  "actionItemCount",
]);

/** Sort order for the HQ analysis column — what needs attention first, with
 *  "we don't know" last, since it is not an outcome. `partial` sits next to
 *  `failed` because it IS a failure, just not a total one. */
const HQ_ORDER: Record<HqAnalysisState, number> = {
  failed: 0,
  partial: 1,
  pending: 2,
  analyzed: 3,
  unknown: 4,
};

/** Sort order for the deep-analysis column. */
const DEEP_ORDER: Record<DeepAnalysisState, number> = {
  failed: 0,
  pending: 1,
  analyzed: 2,
  skipped: 3,
};

/** One row of the `meeting-analysis` read service, as much as this page needs. */
interface MeetingAnalysisProbeRow {
  meetingId: number;
  taskKey?: string | null;
  status?: string | null;
}

/** How many of a meeting's seven analyses landed, and how many errored. */
interface HqAnalysisTally {
  ok: number;
  failed: number;
}

/**
 * Reduce a ServiceStore `find()` response to its rows. `meeting-analysis` is a
 * paginated KnexService, so it answers `{ data, total, … }`; a bare array is
 * tolerated so a non-paginated deployment cannot silently read as zero rows.
 * A `null`/`undefined` response is NOT handled here — see the callers, which
 * must treat it as the swallowed failure it is.
 */
function rowsOf<T>(result: { data?: T[] } | T[] | null | undefined): T[] {
  if (Array.isArray(result)) return result;
  return result?.data ?? [];
}

/**
 * Normalise `read_ai_meetings.analysisState` — GANTRY'S DEEP ANALYSIS, not
 * HQ's. `claimed`, `""` and null all collapse into `pending`: from an
 * operator's seat they are the same fact, the deep run has not finished and
 * nothing has gone wrong. `skipped` is kept because it is what the 2026-08-25
 * backfill wrote, and hiding it would misreport the column rather than fix it.
 */
export function deepAnalysisStateOf(meeting: ReadAiMeeting): DeepAnalysisState {
  switch ((meeting.analysisState ?? "").trim().toLowerCase()) {
    case "analyzed":
      return "analyzed";
    case "failed":
      return "failed";
    case "skipped":
      return "skipped";
    default:
      return "pending";
  }
}

/**
 * Route ViewStore for the **Internal Meetings** list.
 *
 * Rule (web CLAUDE.md): route ViewStores are plain classes with
 * `makeAutoObservable` and do NOT extend ViewStore.
 *
 * THIS STORE HOLDS ITS OWN FIND RESPONSE, and that is deliberate — the same
 * exception `ClientDetailStore.meetings` and the DevOps boards
 * (`ReposStore.repos` / `.projects`) already take for the same reason.
 * `read-ai-meetings` is registered WITHOUT `replaceOnFind`, so its `entities`
 * collection MERGES every find into a cache that four consumers fill from four
 * mutually exclusive scopes (the Meetings list, Client 360's clientId-scoped
 * read, this page's `mappingStatus=internal` read). Rendering that cache while
 * captioning it as the current server list is what kept a meeting on this page
 * after a client was assigned to it: the row had left the server's answer, and
 * the merge cache resurrected it with its stale `mappingStatus`. The list below
 * is therefore the LAST RESPONSE, not the cache. See the C2 note on `load()`.
 *
 * The other thing this store keeps is `analysisTallyByMeetingId`, which is not
 * an entity cache but a derived index (meeting id → how many of its seven
 * analyses landed), built from a probe whose rows are thrown away.
 *
 * WHAT THIS PAGE IS. An internal meeting is one a human marked
 * IntraSync-internal in the Mapping Queue: `mappingStatus === 'internal'`,
 * `clientId` null, permanently. It is still analysed for everything that does
 * not need a client. What it never gets from HQ's OWN extraction is the
 * client-keyed half — no client memory, no distribution, no client attribution,
 * and no action items from that path.
 *
 * ACTION ITEMS ARE STILL SHOWN, and the earlier version of this page was wrong
 * to hide the column. The reasoning then was "an internal meeting produces
 * none, so a column that can only say 0 is noise". That was sound while it was
 * believed and it is false: the DEEP analyser pushes items back for internal
 * meetings too, they are stored with a null client, and they are distributable
 * into the internal DevOps connection. Hiding the column would make this page
 * silent about real work the analysis found. A genuine zero is information; an
 * absent column is not.
 */
class InternalMeetingsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  /** The meeting a "Send back to queue" is in flight for, or null. */
  unmarkingId: number | null = null;
  /** Why the last send-back was refused. Separate from {@link error}, which is
   *  about loading the list: a refused send-back leaves the list intact. */
  unmarkError: string | null = null;

  // Header filters
  searchQuery = "";
  analysisFilter: InternalAnalysisFilter = "all";

  // Column sort (applied client-side over the loaded set, like the filters)
  sortKey: InternalMeetingSortKey = "startTime";
  sortDir: InternalMeetingSortDir = "desc";

  /**
   * Rows the last successful load returned. Null before the first load, and
   * left alone when a load resolves nothing (a swallowed transport error), so a
   * failed refresh cannot quietly retract a cap the UI already reported.
   */
  loadedRowCount: number | null = null;

  /**
   * meeting id → how many of its seven `meeting_analysis` rows are `ok` and how
   * many are not. A derived index over the probe, not an entity cache: the rows
   * themselves (including `outputJson`) are discarded.
   */
  private analysisTallyByMeetingId = new Map<number, HqAnalysisTally>();

  /**
   * Whether the probe above actually answered. FALSE until it does, and false
   * again the moment it fails — which is what turns every chip on the page into
   * `unknown` rather than into a confident "pending" the page cannot support.
   */
  analysisProbeLoaded = false;

  /**
   * Stamp of the newest `load()`. Two realtime events arriving close together
   * fire two overlapping loads; without this, whichever read RESOLVES last wins
   * regardless of which was ASKED last, so an older answer can overwrite a
   * newer one and leave the page stale until the next event. Every write below
   * is gated on still being the newest — including the `finally` that clears
   * the spinner, or a superseded load would hide a live one's progress.
   *
   * Deliberately a stamp rather than an `if (this.loading) return` early exit:
   * that would DROP a reload a later event genuinely needed. Same pattern as
   * DevOpsProjectStore.
   */
  private loadSeq = 0;

  /** Same idea as {@link loadSeq}, for the HQ-analysis probe. It is a second
   *  async read that can interleave on its own, so it carries its own stamp. */
  private probeSeq = 0;

  private unsubscribe: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `read-ai-meetings` ServiceStore. */
  private get meetingsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("read-ai-meetings") ??
        this.rootStore.readAiMeetingsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  /** The registered `meeting-mappings` ServiceStore — the send-back (patch). */
  private get mappingsService(): ServiceStoreType | null {
    return (
      ((this.rootStore.serviceStoreManager?.getStore("meeting-mappings") ??
        this.rootStore.meetingMappingsStore) as unknown as ServiceStoreType | null) ??
      null
    );
  }

  /** The registered `meeting-analysis` ServiceStore — HQ's per-task outputs.
   *  Same accessor MeetingDetailStore uses for the same table. */
  private get analysisService(): ServiceStoreType | null {
    return (
      (this.rootStore.serviceStoreManager?.getStore(
        "meeting-analysis"
      ) as unknown as ServiceStoreType | null) ?? null
    );
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * THE ROWS THE NEWEST SUCCESSFUL `find()` RETURNED — not the ServiceStore's
   * merge cache. A meeting that has left the server's answer (a client was
   * assigned to it, so it is no longer `mappingStatus='internal'`) leaves this
   * list on the next load. Public so tests can seed it without a round trip;
   * only `load()` writes it in production.
   */
  loadedMeetings: ReadAiMeeting[] = [];

  /**
   * The page's population: meetings a human marked internal.
   *
   * `load()` asks the server for `mappingStatus: 'internal'` so it can narrow
   * server-side, but this predicate is applied REGARDLESS. If the API ignores
   * the query param the response holds every meeting, and this is what keeps
   * the page correct. The two paths differ only in how many rows cross the
   * wire.
   */
  get internalMeetings(): ReadAiMeeting[] {
    return this.loadedMeetings.filter((m) => m.mappingStatus === "internal");
  }

  /**
   * HQ's analysis state for one meeting — the page's primary signal.
   *
   * `unknown` is a first-class answer, not a fallback: until the probe has
   * answered, this page genuinely does not know whether HQ analysed a meeting,
   * and saying "pending" would be asserting something it has not read.
   *
   * `partial` is the state the old one-key probe could not represent, and the
   * one that made this page dishonest in both directions — see
   * {@link HQ_ANALYSIS_TASK_KEYS}. Rows that exist but do not fill all seven
   * keys count as partial too: some landed and the rest have not, which is
   * exactly what "partial" says and is never "analyzed".
   */
  hqAnalysisStateOf(meeting: ReadAiMeeting): HqAnalysisState {
    if (!this.analysisProbeLoaded) return "unknown";
    const tally = this.analysisTallyByMeetingId.get(meeting.id);
    if (!tally || tally.ok + tally.failed === 0) return "pending";
    if (tally.ok === 0) return "failed";
    if (tally.failed === 0 && tally.ok === HQ_ANALYSIS_TASK_KEYS.length) {
      return "analyzed";
    }
    return "partial";
  }

  /** Gantry's deep-analysis state for one meeting. Re-exported as a method so
   *  the body never has to reach for the raw column. */
  deepAnalysisStateOf(meeting: ReadAiMeeting): DeepAnalysisState {
    return deepAnalysisStateOf(meeting);
  }

  get filteredMeetings(): ReadAiMeeting[] {
    let result = this.internalMeetings;

    if (this.analysisFilter !== "all") {
      const wanted = this.analysisFilter;
      result = result.filter((m) => this.hqAnalysisStateOf(m) === wanted);
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      // Title and owner only — every other column on this page is a state, and
      // searching text the page does not display is a trap.
      result = result.filter(
        (m) =>
          m.title?.toLowerCase().includes(q) ||
          m.ownerName?.toLowerCase().includes(q) ||
          m.ownerEmail?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * sort last in either direction so "no start time" never floats to the top.
   */
  get sortedMeetings(): ReadAiMeeting[] {
    const key = this.sortKey;
    const valueOf = (m: ReadAiMeeting): SortValue => {
      switch (key) {
        case "title":
          return textValue(m.title);
        case "ownerName":
          return textValue(m.ownerName ?? m.ownerEmail);
        case "startTime":
          return dateValue(m.startTime);
        case "createdAt":
          return dateValue(m.createdAt);
        case "hqAnalysis":
          return HQ_ORDER[this.hqAnalysisStateOf(m)];
        case "deepAnalysis":
          return DEEP_ORDER[deepAnalysisStateOf(m)];
        case "actionItemCount":
          return m.actionItemCount ?? 0;
      }
    };
    return sortRows(this.filteredMeetings, valueOf, this.sortDir);
  }

  // ---- Counts -------------------------------------------------------------

  /** Internal meetings loaded. A lower bound while `isTruncated`. */
  get totalCount(): number {
    return this.internalMeetings.length;
  }

  /**
   * Whether the HQ-analysis counts below mean anything. When false the page
   * must render them as unknown — NOT as zero, which would read as a verdict.
   */
  get analysisKnown(): boolean {
    return this.analysisProbeLoaded;
  }

  private countOf(state: HqAnalysisState): number {
    return this.internalMeetings.filter(
      (m) => this.hqAnalysisStateOf(m) === state
    ).length;
  }

  /** Fully analysed by HQ — all seven `meeting_analysis` rows are `ok`. */
  get analyzedCount(): number {
    return this.countOf("analyzed");
  }

  /** Some of the seven landed and some did not. Counted separately from both
   *  neighbours: folding it into "analyzed" overstates and folding it into
   *  "failed" understates, and each was a live misreport. */
  get partialCount(): number {
    return this.countOf("partial");
  }

  /** Marked internal, no `meeting_analysis` rows yet. */
  get pendingCount(): number {
    return this.countOf("pending");
  }

  /** HQ's analysis ran and NOT ONE of the seven landed. */
  get failedCount(): number {
    return this.countOf("failed");
  }

  // ---- Empty states -------------------------------------------------------

  /**
   * No internal meetings IN THE LOADED SET. Deliberately not phrased as "none
   * exist" — see {@link emptyState}, which is what the UI must branch on.
   */
  get hasNoInternalMeetings(): boolean {
    return this.internalMeetings.length === 0;
  }

  /**
   * Which empty state the table must render, or null when it has rows.
   *
   *  - `error`     — the load failed; nothing is known.
   *  - `filtered`  — internal meetings ARE loaded and the facets hide them.
   *  - `capped`    — none of the loaded meetings are internal, AND the load hit
   *                  the 500-row cap. This page can only see the newest page:
   *                  if the server ignored the `mappingStatus` filter those 500
   *                  rows were meetings of every kind, so a dozen internal
   *                  meetings can sit just outside the window. "Nothing has
   *                  ever been marked internal" is a claim the page cannot
   *                  make here, and it is the exact claim an operator would act
   *                  on.
   *  - `none`      — none loaded and the load was NOT capped, so the page saw
   *                  everything there is and can say so.
   */
  get emptyState(): "error" | "filtered" | "capped" | "none" | null {
    if (this.filteredMeetings.length > 0) return null;
    // `filtered` outranks `error`, the same order MappingQueueStore uses and
    // for the same reason: rows ARE loaded, so the page knows what is here and
    // the filter is why none show. Reporting a failed background reload
    // instead would replace an explanation the operator can act on with one
    // they cannot — and it takes the Clear-filters button away with it,
    // leaving no way out of the filter that hid the rows.
    if (this.internalMeetings.length > 0) return "filtered";
    if (this.error) return "error";
    if (this.isTruncated) return "capped";
    return "none";
  }

  /** There ARE internal meetings; the header's search/filter hide all of them. */
  get noMatches(): boolean {
    return (
      this.internalMeetings.length > 0 && this.filteredMeetings.length === 0
    );
  }

  /** Whether anything is narrowing the list — drives the Clear filters button. */
  get hasActiveFilters(): boolean {
    return this.searchQuery.trim() !== "" || this.analysisFilter !== "all";
  }

  // ---- Load cap (see shared/truncation.ts) --------------------------------

  /** What the last load asked for vs. what came back. */
  get loadCap(): LoadCap {
    return {
      requested: MEETING_INTEL_SERVER_CAP,
      returned: this.loadedRowCount,
    };
  }

  /**
   * True when the load came back at the server's cap — every count on the page
   * is then a lower bound. Note this measures the RESPONSE, not the internal
   * subset, which is what makes it correct either way: whether the server
   * narrowed to internal meetings or handed back 500 rows of everything, a full
   * page means there are older rows this screen never saw.
   */
  get isTruncated(): boolean {
    return isAtCap(this.loadCap);
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setAnalysisFilter(filter: InternalAnalysisFilter) {
    this.analysisFilter = filter;
  }

  /** Reset every facet — the way out of "nothing matches these filters". */
  clearFilters() {
    this.searchQuery = "";
    this.analysisFilter = "all";
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (dates newest-first, text A→Z). */
  setSort(key: InternalMeetingSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  async load() {
    this.ensureSubscribed();
    const seq = ++this.loadSeq;
    this.loading = true;
    this.error = null;
    try {
      // `mappingStatus` is the server-side narrowing added with this page
      // (plan §2.1.4). It is sent as a HINT, not a contract: `internalMeetings`
      // re-applies the same predicate over whatever comes back, so a server
      // that ignores the param costs bandwidth and nothing else.
      // MEETING_INTEL_SERVER_CAP mirrors the service's own hard clamp — it does
      // Math.min($limit, 500), returns a bare array with no total and ignores
      // $skip, so this is one page and there is no second one to ask for.
      const response = await this.meetingsService?.find({
        query: {
          mappingStatus: "internal",
          $limit: MEETING_INTEL_SERVER_CAP,
          $sort: { createdAt: -1 },
        },
      });
      // C1. The registered ServiceStore runs with the default
      // `throwOnError = false`, so a FAILED find does not reject — it records
      // the reason on `errorOnFind` and RESOLVES `null`. Read as "no rows",
      // that rendered "No internal meetings yet" with the API down. Treat a
      // null response exactly like a rejection, with the message the wrapper
      // swallowed (views/devops/shared/swallowedFind.ts).
      if (response == null) {
        throw swallowedFailure(this.meetingsService, "errorOnFind", "Failed to load internal meetings");
      }
      const returned = rowsReturned(response);
      if (seq !== this.loadSeq) return; // a newer load superseded this one
      runInAction(() => {
        // C2. The response REPLACES the list; it is not merged into it. A row
        // the newest answer omits — a meeting that has just been assigned a
        // client — is gone from the page, which is what "these are the
        // internal meetings" has to mean.
        this.loadedMeetings = rowsOf<ReadAiMeeting>(
          response as unknown as { data?: ReadAiMeeting[] } | ReadAiMeeting[]
        );
        if (returned !== null) this.loadedRowCount = returned;
      });
    } catch (err: unknown) {
      if (seq === this.loadSeq) {
        runInAction(() => {
          if (!isSessionEnded(err)) {
            this.error =
              err instanceof Error
                ? err.message
                : "Failed to load internal meetings";
          }
        });
      }
      console.error("Failed to load internal meetings:", err);
    } finally {
      if (seq === this.loadSeq) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
    // After the meetings, ask what HQ actually analysed. Second because it is
    // keyed by the ids the first read produced — and skipped entirely if a
    // newer load is already in flight, which will run its own probe.
    if (seq === this.loadSeq) await this.probeHqAnalysis();
  }

  /**
   * ONE bounded read that answers "what did HQ actually analyse?" for every
   * meeting on screen: all seven `meeting.*` rows for the loaded internal ids.
   *
   * THE BOUND, stated because a read keyed by a list needs one. The table's
   * unique key is `(meetingId, taskKey)` (migration 20260622000002), so the
   * probe can return at most `ids.length × 7` rows and asks for exactly that
   * many — at the page's own 500-meeting cap, 3500 rows, well inside the API's
   * `paginate.max` of 15000. It stays ONE round trip: a per-row read would be
   * N of them, and the seven keys are the difference between a verdict and a
   * guess, not a saving.
   *
   * A failure here leaves `analysisProbeLoaded` false, which renders every HQ
   * chip as `unknown`. That is the honest outcome — the page has not read the
   * table, so it has nothing to report — and it deliberately does NOT set
   * `this.error`, which is about the meetings themselves.
   */
  private async probeHqAnalysis() {
    const seq = ++this.probeSeq;
    const ids = this.internalMeetings.map((m) => m.id);
    if (ids.length === 0) {
      runInAction(() => {
        this.analysisTallyByMeetingId = new Map();
        // Nothing to ask about is a complete answer, not an unknown one.
        this.analysisProbeLoaded = true;
      });
      return;
    }
    const service = this.analysisService;
    if (!service) {
      runInAction(() => {
        this.analysisProbeLoaded = false;
      });
      return;
    }
    try {
      const res = (await service.find({
        query: {
          meetingId: { $in: ids },
          taskKey: { $in: [...HQ_ANALYSIS_TASK_KEYS] },
          $limit: ids.length * HQ_ANALYSIS_TASK_KEYS.length,
        },
      })) as unknown as
        | { data?: MeetingAnalysisProbeRow[] }
        | MeetingAnalysisProbeRow[]
        | null
        | undefined;
      // C1, again: a failed find on this wrapper resolves `null` with the
      // reason on `errorOnFind`. Turning that into an empty row set reported
      // every meeting on the page as "awaiting" — a verdict drawn from a read
      // that never happened. It is the same outcome as a rejection.
      if (res == null) {
        throw swallowedFailure(service, "errorOnFind", "Failed to read HQ analysis");
      }
      const rows = rowsOf<MeetingAnalysisProbeRow>(res);
      // A superseded probe answered about ids that may no longer be on screen;
      // writing it would overwrite the newer answer with an older one.
      if (seq !== this.probeSeq) return;
      const next = new Map<number, HqAnalysisTally>();
      for (const row of rows) {
        if (typeof row?.meetingId !== "number") continue;
        const tally = next.get(row.meetingId) ?? { ok: 0, failed: 0 };
        if ((row.status ?? "").trim().toLowerCase() === "ok") tally.ok += 1;
        else tally.failed += 1;
        next.set(row.meetingId, tally);
      }
      runInAction(() => {
        this.analysisTallyByMeetingId = next;
        this.analysisProbeLoaded = true;
      });
    } catch (err: unknown) {
      // Signed out under this call: what the reset left is the next person's.
      if (isSessionEnded(err)) return;
      // A superseded probe's failure says nothing about the current one.
      if (seq === this.probeSeq) {
        runInAction(() => {
          this.analysisProbeLoaded = false;
        });
      }
      console.error("Failed to read HQ analysis for internal meetings:", err);
    }
  }

  /** Alias used by header refresh / realtime callbacks. */
  async reload() {
    await this.load();
  }

  // ---- Send back to the mapping queue -------------------------------------

  /** Admin only, as the API's `meeting-mappings` patch is. The button is not
   *  offered to anyone the API would refuse. */
  get canUnmark(): boolean {
    return isAdmin(this.rootStore);
  }

  clearUnmarkError() {
    this.unmarkError = null;
  }

  /**
   * Send an internal meeting back to the Mapping Queue, for when it turns out
   * to have been with a client. Contract: `patch(meetingId, { action:
   * 'unmark-internal' })`. The API changes the status and nothing else: the
   * meeting's analyses stay, and the client is picked in the queue, where the
   * assign reprocesses it and moves the work created meanwhile.
   */
  async unmark(meetingId: number): Promise<boolean> {
    this.unmarkingId = meetingId;
    this.unmarkError = null;
    try {
      const payload: MeetingMappingUnmarkPayload = { action: "unmark-internal" };
      // The ServiceStore resolves a refused patch as undefined and keeps the
      // reason on `errorOnPatch`; a nullish result is a refusal, not success.
      const result = await this.mappingsService?.patch(String(meetingId), payload as never);
      if (result == null) {
        throw swallowedFailure(
          this.mappingsService,
          "errorOnPatch",
          "Failed to send the meeting back to the queue"
        );
      }
      await this.reload();
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.unmarkError =
            err instanceof Error ? err.message : "Failed to send the meeting back to the queue";
        }
      });
      return false;
    } finally {
      runInAction(() => {
        this.unmarkingId = null;
      });
    }
  }

  // ---- Realtime -----------------------------------------------------------

  private ensureSubscribed() {
    if (this.unsubscribe) return;
    // Shared realtime bus (src/realtime) — auth is sourced internally from the
    // AuthStore, so a subscription can never be silently tokenless.
    //
    // THE EVENT THIS PAGE EXISTS FOR (C7, 2026-09-03).
    // `meeting-marked-internal` is what puts a row on this page, and until the
    // API emitted it this surface was registered live while having no way to
    // hear its own defining transition: `meeting-mappings` carried no events
    // and `runPipeline`'s internal branch returns before `emitProcessed`, so
    // the list only moved when some LATER transcript or processing event
    // happened to arrive. The other two remain — an internal meeting's
    // analysis is announced by `meeting-processed` — but this is the one that
    // makes a newly marked meeting appear.
    this.unsubscribe = subscribeMeetingIntelEvents(
      this.rootStore,
      (event: MeetingIntelEvent) => {
        if (
          event.kind === "meeting-marked-internal" ||
          // Another admin sent one back to the queue: it leaves this page.
          event.kind === "meeting-unmarked-internal" ||
          event.kind === "meeting-processed" ||
          event.kind === "transcript-received"
        ) {
          void this.reload();
        }
      }
    );
  }

  /** Tear down the realtime subscription (call on route exit). */
  dispose() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.dispose();
    this.loading = false;
    this.error = null;
    this.unmarkingId = null;
    this.unmarkError = null;
    this.searchQuery = "";
    this.analysisFilter = "all";
    this.sortKey = "startTime";
    this.sortDir = "desc";
    this.loadedRowCount = null;
    this.analysisTallyByMeetingId = new Map<number, HqAnalysisTally>();
    this.analysisProbeLoaded = false;
    this.loadSeq += 1;
    this.probeSeq += 1;
    this.unsubscribe = null;
    this.loadedMeetings = [];
  }
}

export default InternalMeetingsStore;
